/**
 * Domain vocabulary shared by the database, the wire protocol and the client.
 *
 * These literals are written into `matches` and `moves` rows and echoed over the
 * socket, so there must be exactly one definition of them. This module has no
 * dependencies beyond the game modules and must never import from `server/`.
 */
import { MAX_ID_LENGTH, MAX_SEAT_VALUE } from '../games/spec/schema.ts';

/**
 * A seat, as stored and as sent on the wire.
 *
 * Deliberately `number` and not a union. This used to re-export Connect 4's
 * `1 | 2`, which quietly made "two players" a fact about the database rather
 * than about one game: a three-seat spec was expressible in the vocabulary, in
 * the interpreter and on the wire, but not in a row. The narrow union still
 * exists where it is true — inside a specific game's engine — and the boundary
 * between the two is `isDeclaredSeat`, checked against the spec being played.
 */
export type Seat = number;

/**
 * The game a request means when it does not say.
 *
 * Connect 4 is the compiled-in reference game, so this is the one id that
 * resolves on a cold deployment with an empty database. It keeps `/api/me` and
 * the leaderboard answerable without a game parameter, which is what stops
 * per-game ratings from becoming a breaking change for every existing caller.
 */
export const DEFAULT_GAME_ID = 'connect4';

/** Mirrors `SeatValueSchema`: seats are 1-based single digits. */
export function isSeatValue(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= MAX_SEAT_VALUE;
}

/**
 * The games compiled into this build. A *catalogue*, not a closed universe: it
 * drives the lobby, but a game id is no longer constrained to it, because the
 * platform's whole point is that games arrive as data.
 */
export const GAME_IDS = ['connect4'] as const;
export type BuiltInGameId = (typeof GAME_IDS)[number];

/**
 * Any game a spec can name.
 *
 * Widened from the old closed enum: a user-authored spec has an id the compiler
 * has never seen, and `matches.game` is already a `text` column with no CHECK
 * constraint for exactly this reason. The shape is still bounded by
 * `isGameId`, so an id stays safe to store, log and put in a URL.
 */
export type GameId = string;

/** Mirrors `IdSchema` in the spec schema: 1..64 characters. */
export function isGameId(value: string): boolean {
  return value.length >= 1 && value.length <= MAX_ID_LENGTH;
}

export const MATCH_STATUSES = ['in_play', 'complete'] as const;
export type MatchStatus = (typeof MATCH_STATUSES)[number];

export const MATCH_RESULTS = ['seat1_win', 'seat2_win', 'draw'] as const;
export type MatchResult = (typeof MATCH_RESULTS)[number];

export const END_REASONS = [
  'four_in_a_row',
  'board_full',
  'resign',
  'abandon',
  'timeout',
] as const;
export type EndReason = (typeof END_REASONS)[number];

export const MOVE_KINDS = ['drop', 'resign', 'abandon'] as const;
export type MoveKind = (typeof MOVE_KINDS)[number];

/**
 * A move's parameters as they are persisted, in the vocabulary of whichever
 * game produced them.
 *
 * One field, because the whole authorable vocabulary is currently one integer
 * parameter per move — a `drop`'s column. It is an object rather than a bare
 * number so that widening the vocabulary is an added key on existing rows,
 * never a rewrite of the append-only log.
 */
export interface MovePayload {
  readonly param: number;
}

/** True for a move that changes the board, as opposed to ending the match. */
export function isGameMoveKind(kind: MoveKind): boolean {
  return kind !== 'resign' && kind !== 'abandon';
}

/**
 * Lifecycle of an authored game version.
 *
 * Only two states, because the interesting boundary is "can this still change".
 * A draft is private and editable; a published version is world-readable and
 * frozen by a database trigger, so a match already in progress can never have
 * its rules altered underneath it.
 */
export const GAME_SPEC_STATUSES = ['draft', 'published'] as const;
export type GameSpecStatus = (typeof GAME_SPEC_STATUSES)[number];

/**
 * The result from the point of view of a seat winning.
 *
 * The result vocabulary is two-seat, which is why this only names seats 1 and
 * 2: `MATCH_RESULTS` and the Elo model both assume a head-to-head. Seat storage
 * is N-seat ahead of that on purpose — the schema should not be the thing that
 * blocks a three-player game — but a third seat cannot be *scored* until the
 * result vocabulary grows, so this asserts rather than inventing an answer.
 */
export function resultForWinner(seat: Seat): MatchResult {
  if (seat === 1) return 'seat1_win';
  if (seat === 2) return 'seat2_win';
  throw new Error(`no match result is defined for a win by seat ${seat}`);
}

/** SQL literal list helper for CHECK constraints, e.g. `'a', 'b'`. */
export function sqlLiteralList(values: readonly string[]): string {
  return values.map((value) => `'${value}'`).join(', ');
}
